import type { IconName } from '@/components/icons';
import { INSTITUTION_ADMIN_ROLES, isInstitutionAdmin, isPlatformAdmin } from '@/auth/session';

/**
 * Who may be offered a destination.
 *
 * A CLOSED SET OF FOUR, not a free list of role codes per entry. The table below is read
 * when deciding whether somebody should see a page, and a column of ad-hoc role arrays
 * becomes impossible to audit — the question "what can a teacher reach?" would mean reading
 * fourteen arrays. Four named audiences can be checked at a glance, and a new role joins an
 * existing audience rather than being sprinkled through the table.
 */
export type Audience = 'EVERYONE' | 'APPROVER' | 'INSTITUTION_ADMIN' | 'PLATFORM_ADMIN';

/**
 * Roles that decide requests, beyond the administrators.
 *
 * Taken from the approver labels the approvals page itself renders. Treating approvals as
 * administrator-only would hide the page from the deans and principals it was built for,
 * which is the opposite of the fault this scoping exists to fix.
 */
const APPROVER_ROLES = ['DEAN', 'PRINCIPAL', 'VICE_PRINCIPAL', 'HOD'] as const;

export interface Destination {
  /** Stable across renders and persisted in recents, so it must not be the label. */
  id: string;
  label: string;
  path: string;
  icon: IconName;
  /** Where it sits, shown as a breadcrumb so two "Users" entries are distinguishable. */
  group: string;
  /** Extra words that should match, for people who search for what a thing does. */
  keywords?: string[];
  /** Creates something, so it is offered under "New" as well as under search. */
  creates?: boolean;
  /**
   * Who may be offered this. Required, with no default — an omitted audience would silently
   * mean "everyone", and the way this list goes wrong is a page added without anybody
   * thinking about who should reach it.
   */
  audience: Audience;
}

/**
 * Everywhere the command palette can send someone.
 *
 * WHY A LIST AND NOT A CRAWL OF THE ROUTER
 * A route table knows paths, not intent. It cannot say that "invite" should find the users
 * page, that Academic Years is where terms live, or that adding a campus is a creation
 * rather than a destination. Those are the things people actually type, and they have to
 * be written down somewhere; here they sit next to the path they resolve to.
 *
 * EVERY ENTRY DECLARES ITS AUDIENCE, and the palette is only ever handed the subset the
 * current role can actually open. Before this, every role was offered the full
 * administrator list: a teacher searching "b" was shown Branding, and following it led to a
 * page they cannot use. A result that ends in a permission error is worse than no result,
 * because it reads as a system fault rather than as a boundary.
 *
 * Note how few destinations a non-administrator has. That is not an oversight — this
 * console genuinely has four pages open to every role, because a teacher's actual work
 * happens in EduCore. Padding the list with pages that would reject them would recreate the
 * exact fault being fixed.
 */
const ALL_DESTINATIONS: readonly Destination[] = [
  // Open to every role, and the only routes outside /admin that exist.
  { id: 'home', label: 'Home', path: '/', icon: 'home', group: 'Overview', keywords: ['start', 'dashboard', 'insights', 'accueil', 'tableau de bord'], audience: 'EVERYONE' },
  { id: 'products', label: 'Products', path: '/products', icon: 'products', group: 'Workspace', keywords: ['educore', 'launch', 'subscription', 'apps', 'produits', 'lancer'], audience: 'EVERYONE' },
  { id: 'notifications', label: 'Notifications', path: '/notifications', icon: 'notifications', group: 'Overview', keywords: ['alerts', 'inbox', 'notifications', 'alertes'], audience: 'EVERYONE' },

  // The journey timeline works for every role: one's own day, or a linked
  // student's for guardians. Scan & Verify is offered broadly because scanning
  // staff (teachers, librarians, gate staff) are not a role the client can
  // enumerate; the server refuses per purpose.
  { id: 'journey', label: 'School Day', path: '/journey', icon: 'clock', group: 'Workspace', keywords: ['journey', 'timeline', 'child', 'arrival', 'bus', 'journée', 'trajet', 'arrivée', 'enfant'], audience: 'EVERYONE' },
  { id: 'verify', label: 'Scan & Verify', path: '/verify', icon: 'checkmark', group: 'Operations', keywords: ['scan', 'card', 'qr', 'identity', 'gate', 'attendance', 'vérifier', 'carte', 'identité', 'présence'], audience: 'EVERYONE' },

  // Approvers: administrators plus the deans and principals who decide requests.
  { id: 'approvals', label: 'Approvals', path: '/approvals', icon: 'checklist', group: 'Operations', keywords: ['request', 'decide', 'pending', 'sign off', 'approbations', 'valider'], audience: 'APPROVER' },

  { id: 'dashboard', label: 'Dashboard', path: '/admin/institution', icon: 'pulse', group: 'Overview', keywords: ['home', 'summary', 'overview', 'tableau de bord', 'aperçu'], audience: 'INSTITUTION_ADMIN' },
  { id: 'profile', label: 'Institution Profile', path: '/admin/institution/profile', icon: 'profile', group: 'Institution', keywords: ['school', 'details', 'address', 'legal name', 'currency', 'contact', 'profil', 'école', 'devise', 'monnaie'], audience: 'INSTITUTION_ADMIN' },
  { id: 'campuses', label: 'Campuses', path: '/admin/institution/campuses', icon: 'campus', group: 'Institution', keywords: ['site', 'branch', 'location', 'campus', 'site', 'succursale'], creates: true, audience: 'INSTITUTION_ADMIN' },
  { id: 'academic-years', label: 'Academic Years', path: '/admin/institution/academic-years', icon: 'academic', group: 'Institution', keywords: ['term', 'session', 'semester', 'calendar', 'années académiques', 'année scolaire', 'trimestre', 'semestre'], creates: true, audience: 'INSTITUTION_ADMIN' },
  { id: 'academic-structure', label: 'Academic Structure', path: '/admin/institution/academic-structure', icon: 'academic', group: 'Institution', keywords: ['classes', 'class groups', 'units', 'streams', 'structure académique', 'classes', 'groupes', 'unités'], audience: 'INSTITUTION_ADMIN' },
  { id: 'users', label: 'Users', path: '/admin/institution/users', icon: 'users', group: 'People', keywords: ['staff', 'invite', 'teacher', 'account', 'utilisateurs', 'inviter', 'enseignant'], creates: true, audience: 'INSTITUTION_ADMIN' },
  { id: 'roles', label: 'Roles', path: '/admin/institution/roles', icon: 'role', group: 'People', keywords: ['permission', 'access', 'assign', 'rôles', 'permissions', 'accès'], audience: 'INSTITUTION_ADMIN' },
  { id: 'credentials', label: 'Credentials', path: '/admin/institution/credentials', icon: 'role', group: 'People', keywords: ['card', 'id card', 'qr', 'identity', 'issue', 'badge', 'identifiants', 'carte', 'identité'], creates: true, audience: 'INSTITUTION_ADMIN' },
  { id: 'branding', label: 'Branding', path: '/admin/institution/branding', icon: 'branding', group: 'Configuration', keywords: ['logo', 'colour', 'color', 'theme', 'image de marque', 'couleur', 'thème'], audience: 'INSTITUTION_ADMIN' },
  { id: 'integrations', label: 'Integrations', path: '/admin/institution/integrations', icon: 'integration', group: 'Configuration', keywords: ['connection', 'api', 'switch', 'educore', 'intégrations', 'connexion'], audience: 'INSTITUTION_ADMIN' },
  { id: 'admin-products', label: 'Products', path: '/admin/institution/products', icon: 'products', group: 'Configuration', keywords: ['subscriptions', 'launch', 'educore', 'modules', 'apps', 'produits', 'abonnements', 'modules'], audience: 'INSTITUTION_ADMIN' },
  { id: 'experience', label: 'Experience', path: '/admin/institution/experience', icon: 'globe', group: 'Configuration', keywords: ['vocabulary', 'terminology', 'language', 'locale', 'calendar', 'expérience', 'vocabulaire', 'terminologie', 'langue', 'calendrier'], audience: 'INSTITUTION_ADMIN' },
  { id: 'services', label: 'Services', path: '/admin/institution/services', icon: 'settings', group: 'Configuration', keywords: ['fees', 'pricing', 'billing', 'custom', 'options', 'services', 'frais', 'tarification', 'facturation'], audience: 'INSTITUTION_ADMIN' },
  { id: 'workflows', label: 'Workflows', path: '/admin/institution/workflows', icon: 'checklist', group: 'Configuration', keywords: ['approval', 'request', 'process', 'flows', 'flux', 'processus', 'approbation'], audience: 'INSTITUTION_ADMIN' },
  { id: 'data-import', label: 'Data Import', path: '/admin/institution/data-import', icon: 'plus', group: 'Configuration', keywords: ['import', 'csv', 'upload', 'migrate', 'excel', 'import de données', 'importation', 'csv'], audience: 'INSTITUTION_ADMIN' },
  { id: 'parent-access', label: 'Parent Access', path: '/admin/institution/parent-access', icon: 'parent', group: 'Configuration', keywords: ['guardian', 'portal', 'parents', 'app access', 'accès parents', 'parents', 'tuteur'], audience: 'INSTITUTION_ADMIN' },
  { id: 'announcements', label: 'Announcements', path: '/admin/institution/announcements', icon: 'announcement', group: 'Operations', keywords: ['notice', 'message', 'broadcast', 'annonces', 'message', 'diffuser'], creates: true, audience: 'INSTITUTION_ADMIN' },
  { id: 'tasks', label: 'Tasks', path: '/admin/institution/tasks', icon: 'tasks', group: 'Operations', keywords: ['todo', 'action', 'outstanding', 'tâches', 'à faire', 'actions'], audience: 'INSTITUTION_ADMIN' },
  { id: 'reports', label: 'Reports', path: '/admin/institution/reports', icon: 'report', group: 'Operations', keywords: ['export', 'analytics', 'download', 'rapports', 'analyses', 'export'], audience: 'INSTITUTION_ADMIN' },
  { id: 'marketplace', label: 'Marketplace', path: '/admin/institution/marketplace', icon: 'products', group: 'Operations', keywords: ['merchant', 'vendor', 'shop', 'marché', 'commerçant', 'boutique'], audience: 'INSTITUTION_ADMIN' },
  { id: 'audit', label: 'Audit Trail', path: '/admin/institution/audit', icon: 'history', group: 'Operations', keywords: ['history', 'log', 'activity', 'trail', 'audit', 'historique', 'journal', 'activité'], audience: 'INSTITUTION_ADMIN' },
  { id: 'security', label: 'Security', path: '/admin/institution/security', icon: 'warning', group: 'Operations', keywords: ['password', 'mfa', 'session', 'authentication', '2fa', 'sécurité', 'mot de passe', 'authentification'], audience: 'INSTITUTION_ADMIN' },
  { id: 'support', label: 'Support', path: '/admin/institution/support', icon: 'mail', group: 'Operations', keywords: ['help', 'contact', 'ticket', 'assistance', 'aide', 'contact', 'assistance'], audience: 'INSTITUTION_ADMIN' },
];

export interface DestinationScope {
  /** The active context's role code. Undefined while the context is still resolving. */
  roleCode: string | undefined;
  /** Realm roles from the session, which is where platform administration is carried. */
  realmRoles: readonly string[] | undefined;
}

/**
 * The destinations one person may be offered, in one context.
 *
 * FAILS CLOSED. An unrecognised role — a code this build has never heard of, or a context
 * that has not resolved — gets the EVERYONE set and nothing more. The alternative, treating
 * unknown as unrestricted, means every future role ships seeing the administrator's pages
 * until somebody remembers to add it here.
 *
 * THIS IS NOT AUTHORISATION and must never be mistaken for it. The server authorises every
 * one of these pages independently; hiding a destination stops somebody being invited into
 * a wall, it does not stop them typing the address. Both are needed, and only one of them
 * is a security control.
 */
export function destinationsFor(scope: DestinationScope): readonly Destination[] {
  const administers = isInstitutionAdmin(scope.roleCode);
  const platform = isPlatformAdmin(scope.realmRoles);
  const approves =
    administers ||
    platform ||
    (scope.roleCode !== undefined && APPROVER_ROLES.includes(scope.roleCode as never));

  return ALL_DESTINATIONS.filter((destination) => {
    switch (destination.audience) {
      case 'EVERYONE':
        return true;
      case 'APPROVER':
        return approves;
      case 'INSTITUTION_ADMIN':
        return administers || platform;
      case 'PLATFORM_ADMIN':
        return platform;
      default:
        // Unreachable while Audience stays a closed union, and deliberately denies rather
        // than allows if a new member is ever added without updating this switch.
        return false;
    }
  });
}

/** Every id in the catalogue, for tests and for validating persisted history. */
export const ALL_DESTINATION_IDS: readonly string[] = ALL_DESTINATIONS.map((d) => d.id);

/**
 * Retained for the institution-admin view, expressed through the same scoping function so
 * there is one definition of what an administrator may reach rather than two that drift.
 */
export const INSTITUTION_DESTINATIONS: readonly Destination[] = destinationsFor({
  roleCode: INSTITUTION_ADMIN_ROLES[0],
  realmRoles: [],
});

/**
 * Ranks a destination against what has been typed.
 *
 * Returns null for no match, and a lower number for a better one, so callers sort
 * ascending. The ordering is deliberate rather than a single fuzzy score: a label that
 * STARTS with the query is almost always what was meant, and burying it beneath a keyword
 * match on an unrelated page is the thing that makes a palette feel wrong.
 */
export function rank(destination: Destination, query: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  const label = destination.label.toLowerCase();
  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 2;
  if (destination.group.toLowerCase().startsWith(q)) return 3;
  if (destination.keywords?.some((k) => k.toLowerCase().startsWith(q))) return 4;
  if (destination.keywords?.some((k) => k.toLowerCase().includes(q))) return 5;

  // Initials, so "ay" finds Academic Years. Last because it matches loosely and would
  // otherwise crowd out exact prefixes.
  const initials = destination.label
    .split(/\s+/)
    .map((word) => word[0]?.toLowerCase() ?? '')
    .join('');
  if (initials.startsWith(q)) return 6;

  return null;
}
